<template>
  <div class="page">
    <BackNav :title="$t('recharge.title')" />
    <div class="form-card">
      <van-form @submit="onSubmit">
        <!-- 转入方式选择 -->
        <div class="field-wrap">
          <div class="field-label">{{ $t('recharge.method') }}</div>
          <van-field
            v-model="method"
            is-link
            readonly
            name="method"
            :placeholder="$t('recharge.methodPlaceholder')"
            @click="showPayMethod = true"
            :rules="[{ required: true, message: $t('recharge.methodRequired') }]"
            :border="false"
          />
          <van-popup v-model:show="showPayMethod" position="bottom" round>
            <van-picker :columns="topUpList" @confirm="onConfirm" @cancel="showPayMethod = false" />
          </van-popup>
        </div>

        <!-- 支付信息卡:选好方式后显示收款人/收款账号 + 用户手机(可复制) -->
        <div v-if="pickedTopUp" class="pay-info">
          <div class="pay-info__title">
            <span class="tag">{{ $t('recharge.paymentInfo') }}</span>
          </div>

          <div v-if="pickedTopUp.accountName" class="pay-info__row">
            <div class="k">{{ $t('recharge.accountName') }}</div>
            <div class="v">{{ pickedTopUp.accountName }}</div>
          </div>

          <div v-if="pickedTopUp.accountNo" class="pay-info__row">
            <div class="k">{{ $t('recharge.accountNo') }}</div>
            <div class="v mono">{{ pickedTopUp.accountNo }}</div>
            <div class="btn" @click.stop="onCopy(pickedTopUp.accountNo)">{{ $t('common.copy') }}</div>
          </div>

          <div v-if="userMobile" class="pay-info__row">
            <div class="k">{{ $t('recharge.yourMobile') }}</div>
            <div class="v mono">{{ userMobile }}</div>
            <div class="btn" @click.stop="onCopy(userMobile)">{{ $t('common.copy') }}</div>
          </div>
        </div>

        <!-- SINPE 描述必填提示 -->
        <div v-if="isSinpeMethod" class="sinpe-notice">
          <div class="sinpe-notice__title">
            <span class="dot">!</span>
            <span>{{ $t('recharge.sinpeNoticeTitle') }}</span>
          </div>
          <div class="sinpe-notice__body">{{ $t('recharge.sinpeNoticeBody') }}</div>
          <div class="sinpe-notice__field">
            <span class="k">Descripción (opcional):</span>
            <span class="v">{{ userMobile || '88888888' }}</span>
          </div>
          <div class="sinpe-notice__tail">{{ $t('recharge.sinpeNoticeTail') }}</div>
        </div>

        <div class="field-wrap">
          <div class="field-label">{{ $t('recharge.amount') }}</div>
          <van-field
            v-model="amount"
            name="amount"
            type="number"
            :placeholder="$t('recharge.amountPlaceholder')"
            :rules="[{ required: true, message: $t('recharge.amountPlaceholder') }]"
            :border="false"
          />
        </div>

        <van-button round block class="brand-btn submit-btn" :loading="loading" native-type="submit">
          {{ $t('common.confirm') }}
        </van-button>
      </van-form>
    </div>

    <!-- 提交后的摘要弹窗(复用组件) -->
    <RechargeSummaryDialog
      v-model:show="showSummary"
      :info="submitted"
      mode="submitted"
      @confirm="closeSummary"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import BackNav from '@/components/BackNav';
import RechargeSummaryDialog from '@/components/RechargeSummaryDialog.vue';
import { useRouter } from 'vue-router';
import { Toast } from 'vant';
import { UserService, TopUpService } from '@/services';
import { useI18n } from 'vue-i18n';
import { storage, copyText } from '@/utils';
import { authMobile } from '@/constants';
const { t } = useI18n();
const method = ref('');
const methodVal = ref('');
const amount = ref();
const showPayMethod = ref(false);
const topUpListRaw = ref([]);
const loading = ref(false);
const userMobile = ref(storage.getItem(authMobile) || '');
const router = useRouter();

const showSummary = ref(false);
const submitted = ref({});

const topUpList = computed(() =>
  topUpListRaw.value.map((item) => ({ value: item.id, text: item.name }))
);

const pickedTopUp = computed(() =>
  topUpListRaw.value.find((x) => x.id === methodVal.value)
);

const isSinpeMethod = computed(() =>
  method.value && method.value.toLowerCase().includes('sinpe')
);

const initTopUpList = async () => {
  const { result } = await TopUpService.topUpAllListApi();
  topUpListRaw.value = result || [];
};

const onConfirm = (item) => {
  method.value = item.text;
  methodVal.value = item.value;
  showPayMethod.value = false;
};

const onCopy = async (text) => {
  const ok = await copyText(text);
  if (ok) Toast.success(t('common.copied'));
  else Toast.fail(t('common.copyFailed'));
};

const onSubmit = async () => {
  if (/^\d+$/.test(amount.value) && amount.value < 100) {
    Toast.fail(t('recharge.minAmount'));
    return;
  }
  loading.value = true;
  try {
    const { code, result } = await UserService.toUpFeeApi({
      amount: Number(amount.value),
      method: methodVal.value,
    });
    if (code !== 0) return;
    submitted.value = {
      orderId: result?.id || '',
      methodName: method.value,
      accountName: pickedTopUp.value?.accountName || '',
      accountNo: pickedTopUp.value?.accountNo || '',
      amount: Number(amount.value),
      mobile: userMobile.value,
      isSinpe: isSinpeMethod.value,
    };
    showSummary.value = true;
  } finally {
    loading.value = false;
  }
};

const closeSummary = () => {
  showSummary.value = false;
  setTimeout(() => router.go(-1), 200);
};

onMounted(() => {
  initTopUpList();
});
</script>

<style lang="scss" scoped>
.page {
  min-height: 100vh;
  background: var(--bg-page);
}
.form-card {
  margin: 24px 20px;
  background: #fff;
  border-radius: 24px;
  padding: 40px 32px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
}
.field-wrap {
  margin-bottom: 24px;
  .field-label {
    font-size: 26px;
    color: var(--text-secondary);
    margin-bottom: 12px;
    font-weight: 500;
  }
  :deep(.van-cell) {
    background: #f5f7fa;
    border-radius: 16px;
    padding: 24px 28px;
    font-size: 30px;
    &::after { display: none; }
  }
  :deep(.van-field__control) {
    color: var(--text-primary);
    font-size: 30px;
    &::placeholder { color: #a0aec0; }
  }
}
.submit-btn {
  height: 96px !important;
  margin-top: 32px !important;
  font-size: 32px !important;
}

.pay-info {
  margin: 0 0 24px 0;
  padding: 24px 24px;
  background: linear-gradient(135deg, #f5f3ff, #ede9fe);
  border: 2px solid #ddd6fe;
  border-radius: 20px;

  &__title {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 16px;
    .tag {
      display: inline-block;
      padding: 6px 20px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: #fff;
      border-radius: 999px;
      font-size: 22px;
      font-weight: 600;
    }
  }
  &__row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 16px;
    padding: 16px 0;
    border-bottom: 1px dashed rgba(102, 126, 234, 0.2);
    &:last-child { border-bottom: none; }

    .k {
      font-size: 24px;
      color: #4b5563;
      font-weight: 500;
    }
    .v {
      font-size: 30px;
      color: #1f2937;
      font-weight: 700;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .v.mono {
      font-family: 'Menlo', 'Consolas', 'Courier New', monospace;
      color: #764ba2;
      letter-spacing: 1px;
    }
    .btn {
      display: inline-block;
      padding: 12px 24px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: #fff;
      border-radius: 999px;
      font-size: 22px;
      font-weight: 600;
      cursor: pointer;
      &:active { opacity: 0.85; transform: scale(0.98); }
    }
  }
}

.sinpe-notice {
  margin: 0 0 32px 0;
  padding: 28px 24px;
  background: linear-gradient(135deg, #fff8e1, #ffecb3);
  border-left: 8px solid #f59e0b;
  border-radius: 20px;

  &__title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 28px;
    font-weight: 700;
    color: #92400e;
    margin-bottom: 16px;
    .dot {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      background: #f59e0b;
      color: #fff;
      border-radius: 50%;
      font-weight: 700;
    }
  }
  &__body {
    font-size: 26px;
    color: #78350f;
    line-height: 1.5;
    margin-bottom: 16px;
  }
  &__field {
    background: #fff;
    border-radius: 12px;
    padding: 20px 24px;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    font-size: 28px;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
    .k { color: #78350f; font-weight: 500; }
    .v {
      color: #764ba2;
      font-family: 'Menlo', 'Consolas', monospace;
      font-weight: 700;
      font-size: 30px;
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
      padding: 4px 16px;
      border-radius: 8px;
    }
  }
  &__tail {
    margin-top: 14px;
    font-size: 22px;
    color: #78350f;
    line-height: 1.5;
    opacity: 0.8;
  }
}
</style>
